import { useState, useEffect } from 'react';
import CreateFormTopBar from '../../../Components/TopBar/CreateFormTopBar';
import { TOPBAR_CONFIGS } from '../../../Components/TopBar/topBarConfigs';
import Select from 'react-select';
import ChecklistService from '../../../AppSettings/services/ChecklistService';
import MatterService from '../../../AppSettings/services/MatterService';
import useActiveEmployeeId from '../../../AppSettings/hooks/useActiveEmployeeId';

import './CreateChecklistsPage.css';
import { CreateChecklistRequest, CreateChecklistResponse } from '../../../Models/Checklist/ChecklistRequestResponseModels';
import { SelectOption } from '../../../Models/Form/FormModels';
import { Matter } from '../../../Models/Matter/MatterModels';

interface ChecklistFormData extends CreateChecklistRequest {
  duedate: string;
}

const CreateChecklistPage = () => {
  const employeeId = useActiveEmployeeId();

  const [formData, setFormData] = useState<ChecklistFormData>({
    description: '',
    matterid: '',
    duedate: '',
    employeeid: '', // Maybe needed?
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [matters, setMatters] = useState<SelectOption[]>([]);
  const [loadingMatters, setLoadingMatters] = useState<boolean>(true);

  useEffect(() => {
    if (employeeId) {
      setFormData((prev) => ({ ...prev, employeeid: employeeId }));
    }
  }, [employeeId]);

  useEffect(() => {
    const fetchMatters = async () => {
      try {
        const data: Matter[] = await MatterService.getMatters();
        const options: SelectOption[] = data.map((matter) => ({
          value: matter.matterid,
          label: `${matter.matterid}: ${matter.mattername || ''}`,
        }));
        setMatters(options);
      } catch (error) {
        console.error('Error fetching matters:', error);
        setError('Failed to load matters.');
      } finally {
        setLoadingMatters(false);
      }
    };

    fetchMatters();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleMatterChange = (selectedOption: SelectOption | null) => {
    setFormData({
      ...formData,
      matterid: selectedOption ? selectedOption.value : '',
    });
  };

  const handleChecklistCreation = async () => {
    setLoading(true);
    setError('');

    try {
      console.log(formData);
      const payload: CreateChecklistRequest = {
        description: formData.description,
        matterid: formData.matterid || null,
        employeeid: formData.employeeid || null,
        duedate: formData.duedate || null,
      };
      const response: CreateChecklistResponse = await ChecklistService.addChecklistItem(payload);
      if (response.message?.includes('error')) throw new Error(response.message);
      alert('Checklist item added successfully!');
      setFormData({
        description: '',
        matterid: '',
        duedate: '',
        employeeid: '', // maybe needed?
      });
    } catch (error) {
      console.error('Error adding checklist item:', error);
      setError('Failed to add the checklist item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="Tasks-Page">
      <CreateFormTopBar
        config={TOPBAR_CONFIGS.TASKS_CHECKLIST}
        onSave={handleChecklistCreation}
      />
      <div
        className="Tasks-entire-form-container"
        style={{ justifyContent: 'center' }}
      >
        <div className="Tasks-form-container">
          <div className="Tasks-form-group">
            <label className="Tasks-form-group-label">Matter ID</label>
            <Select
              options={matters}
              value={
                matters.find((matter) => matter.value === formData.matterid) ||
                null
              }
              onChange={handleMatterChange}
              isLoading={loadingMatters}
              placeholder="Select or search a matter..."
              isClearable
            />
          </div>

          <div className="Tasks-form-group">
            <label className="Tasks-form-group-label">Description</label>
            <textarea
              name="description"
              placeholder="Enter checklist description"
              value={formData.description}
              onChange={handleInputChange}
            />
          </div>

          <div style={{ paddingBottom: '1em' }}>
            <div className="Tasks-item2">
              <label className="Tasks-form-group-label">Due Date</label>
              <div className="Tasks-input-icon">
                <input
                  type="date"
                  name="duedate"
                  value={formData.duedate}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}
          {loading && (
            <div className="loading-message">Adding checklist item...</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateChecklistPage;